import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { UserService } from '../../../shared/service/user/user.service';
import { Tokens } from '../../model/tokens';
import { CryptojsService } from 'src/app/shared/service/cryptojs/cryptojs.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public static JWT_TOKEN = 'JWT_TOKEN';
  public static REFRESH_TOKEN = 'REFRESH_TOKEN';
  public static EXPIRES_ON = 'EXPIRES_ON';
  public static REFRESH_TOKEN_TIME = 'REFRESH_TOKEN_TIME';

  private static TOKEN_ENDPOINT = 'token';
  private static LOGIN_ENDPOINT = 'login';
  private static REFRESH_ENDPOINT = 'refresh-token';

  private static LOGOUT_ENDPOINT = 'logout';

  private userLoggedIn = new Subject<boolean>();

  serverUrl: string;

  headers = new HttpHeaders({
    'Content-Type': 'application/json',
    responseType: 'json',
  });

  constructor(
    private http: HttpClient,
    private _userService: UserService,
    private appService: AppService,
    private cryptoJs: CryptojsService,
  ) {
    this.userLoggedIn.next(this.isLoggedIn());
  }

  //? User Logged in getter and setter start
  getUserLoggedIn(): Observable<boolean> {
    return this.userLoggedIn.asObservable();
  }

  setUserLoggedIn(loggedIn: boolean) {
    this.userLoggedIn.next(loggedIn);
  }
  //! User Logged in getter and setter end

  //? Refresh token time getter and setter start
  getRefreshTokenTime(): string {
    return localStorage.getItem(AuthService.REFRESH_TOKEN_TIME);
  }

  setRefreshTokenTime(timeInSeconds: number) {
    localStorage.setItem(AuthService.REFRESH_TOKEN_TIME, timeInSeconds.toString());
  }

  removeRefreshTokenTime() {
    localStorage.removeItem(AuthService.REFRESH_TOKEN_TIME);
  }
  //! User Logged in getter and setter end

  // returns observable of true if correct credentials otherwise observable of error title
  getToken(userCreds: any): Observable<boolean | string> {
    return this.http.post<any>(`${this.appService.getTokenUrl()}/${AuthService.TOKEN_ENDPOINT}`, userCreds, {
      headers: this.headers
    }).pipe(
      // Todo - set expiry time
      tap((res) => {
        this.storeTokens(res)
      }),
      map(() => true),
      catchError((err) => {
        return of(err.error.title)
      })
    )
  }

  loginUser(postData: string): Observable<boolean> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      responseType: 'json',
      Authorization: `Bearer ${this.getJwtToken()}`,
      userid: '0x020000001DB809DF0B512C5D1EDC5E3BC8D2FC50E0CD9DD8BB14A102179A12A8B34676CB'
    });
    return this.http.post<any>(
      `${this.appService.getTokenUrl()}/${AuthService.LOGIN_ENDPOINT}`,
      postData,
      {headers: headers}
    ).pipe(
      tap((loginDetails) => {
        if (loginDetails.ok) {
          const { userFullName, userNo, productAccess } = loginDetails.responseData;
          this._userService.setUserEmail(JSON.parse(postData).username);
          this._userService.setUserName(userFullName);
          this._userService.setUserNo(userNo);
          this._userService.setProductAccess(productAccess);
          this.setUserLoggedIn(true);
        } else {
          throw new Error(loginDetails.message);
        }
      }),
      map(() => true),
      catchError((err) => {
        return of(err)
      })
    )
  }

  loginUserUsingQueryParams(params): Observable<string | boolean> {
    const tokens = new Tokens;
    tokens.accessToken = params.accessToken;
    tokens.refreshToken = params.refreshToken;
    this.storeTokens(tokens);
    const loginPostData = {
      username: params.userName,
      productID: params.productID
    }
    return this.loginUser(JSON.stringify(loginPostData))
  }

  refreshToken() {
    const data = {
      RefreshToken: this.getRefreshToken(),
    };
    return this.http.post<any>(`${this.appService.getTokenUrl()}/${AuthService.REFRESH_ENDPOINT}`, data).pipe(
      tap((tokens: Tokens) => {
        this.storeTokens(tokens);
      })
    )
  }

  getExpiresIn(): string {
    return this.appService.getRefreshTokenTime();
  }

  logout(): Observable<boolean> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      responseType: 'json',
      Authorization: `Bearer ${this.getJwtToken()}`,
      client: localStorage.getItem('SUB_DOMAIN'),
      userId: this._userService.getUserNo(),
      env: localStorage.getItem('env'),
    });
    const payload = {
      refreshToken: this.getRefreshToken(),
      accessToken: this.getJwtToken(),
      userId: this._userService.getUserNo(),
      userAgent: navigator.userAgent,
    };
    const encryptedPayload = {
      logoutRequest: this.cryptoJs.encrypt(JSON.stringify(payload))
    }
    this.http.post(`${this.appService.getTokenUrl()}/${AuthService.LOGOUT_ENDPOINT}`, encryptedPayload, { headers })
      .subscribe({
        next: res => console.log(res),
        error: err => console.log(err)
    })
    this.setUserLoggedIn(false);
    this.doLogoutUser();
    return of(true);
  }

  isLoggedIn(): boolean {
    const loggedIn: boolean = !!this.getJwtToken();
    return loggedIn;
  }

  getJwtToken(): string {
    return localStorage.getItem(AuthService.JWT_TOKEN);
  }

  getExpiresOn(): number {
    return JSON.parse(localStorage.getItem(AuthService.EXPIRES_ON));
  }

  private doLogoutUser() {
    this._userService.removeUserName();
    this._userService.removeUserNo();
    this.removeTokens();
  }

  private getRefreshToken() {
    return localStorage.getItem(AuthService.REFRESH_TOKEN);
  }

  storeExpiryTime(expiresIn: string) {
    const currentTime = Math.floor(Date.now() / 1000)
    const expiresInTime = JSON.parse(expiresIn);
    const expiresOn = currentTime + expiresInTime;
    localStorage.setItem(
      AuthService.EXPIRES_ON,
      expiresOn
    );
  }

  isNewTokenRequired(): boolean {
    let expiresOn: number = this.getExpiresOn();
    let currentTime: number = new Date().getTime();
    return currentTime >= expiresOn;
  }

  storeJwtToken(token: string) {
    localStorage.setItem(AuthService.JWT_TOKEN, token);
  }

  storeExpiresOn(time: string) {
    localStorage.setItem(AuthService.EXPIRES_ON, time);
  }

  private storeTokens(tokens: Tokens) {
    localStorage.setItem(AuthService.JWT_TOKEN, tokens.accessToken);
    localStorage.setItem(AuthService.REFRESH_TOKEN, tokens.refreshToken);
    this.storeExpiryTime(this.appService.getRefreshTokenTime());
  }

  private removeTokens() {
    localStorage.removeItem(AuthService.JWT_TOKEN);
    localStorage.removeItem(AuthService.REFRESH_TOKEN);
    localStorage.removeItem(AuthService.EXPIRES_ON);
  }
}
